export function faqSearchInput() {
  function debounce(fn, wait){ let t; return function(...a){ clearTimeout(t); t=setTimeout(()=>fn.apply(this,a),wait); }; }
  function qs(r,s){ return (r||document).querySelector(s); }
  function qsa(r,s){ return Array.from((r||document).querySelectorAll(s)); }

  const ACTIVE            = 'active';
  const CHILD_ANCHOR_SEL  = '.farlo-ui-faq__child-list a[href^="#"]';
  const FAQ_GROUP_SEL     = '.farlo-ui-faq__group';
  const TAB_SEL           = '[data-farlo-ui-tab], [role="tab"], .tab-label';
  const PANEL_SEL         = '[role="tabpanel"], .tab-panel';

  function getHeaderOffsetPx(){
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    const px  = parseFloat(raw || '0');
    return Number.isFinite(px) ? px : 0;
  }
  function smoothScrollTo(el){
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - getHeaderOffsetPx();
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  function getTabsAndPanels(wrap){
    const tabs   = Array.from(wrap.querySelectorAll(TAB_SEL));
    const panels = Array.from(wrap.querySelectorAll(PANEL_SEL));
    return { tabs, panels };
  }

  function setActiveTabByButton(wrap, tabEl){
    const { tabs, panels } = getTabsAndPanels(wrap);
    const targetId = tabEl.getAttribute('aria-controls');
    let targetPanel = null;

    if (targetId) {
      targetPanel = wrap.querySelector('#' + CSS.escape(targetId));
    } else {
      const idx = tabs.indexOf(tabEl);
      if (idx >= 0) targetPanel = panels[idx] || null;
    }

    tabs.forEach(t => {
      const is = t === tabEl;
      t.classList.toggle(ACTIVE, is);
      if (t.hasAttribute('aria-selected')) {
        t.setAttribute('aria-selected', is ? 'true' : 'false');
      }
    });

    panels.forEach(p => {
      const is = p === targetPanel;
      p.classList.toggle(ACTIVE, is);
    });
  }

  function ensureFirstTabActive(wrap){
    const { tabs, panels } = getTabsAndPanels(wrap);
    if (!tabs.length || !panels.length) return false;

    // Deep-link support
    const hash = window.location.hash;
    if (hash && hash.startsWith('#')) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        const panel = target.closest(PANEL_SEL);
        if (panel && wrap.contains(panel)) {
          const id   = panel.id;
          const tab  = tabs.find(t => t.getAttribute('aria-controls') === id) || tabs[panels.indexOf(panel)];
          if (tab) { setActiveTabByButton(wrap, tab); return true; }
        }
      }
    }

    const hasActiveTab   = tabs.some(t => t.classList.contains(ACTIVE));
    const hasActivePanel = panels.some(p => p.classList.contains(ACTIVE));

    if (!hasActiveTab && tabs[0]) setActiveTabByButton(wrap, tabs[0]);
    else if (hasActiveTab && !hasActivePanel) {
      const activeTab = tabs.find(t => t.classList.contains(ACTIVE));
      if (activeTab) setActiveTabByButton(wrap, activeTab);
    }
    return true;
  }

  function enforceFirstTabAfterPaint(wrap){
    const kicks = [0, 1, 16, 100];
    kicks.forEach(ms => {
      const fn = () => ensureFirstTabActive(wrap);
      if (ms === 0) fn();
      else setTimeout(fn, ms);
    });
  }

  function bindTabDelegation(wrap){
    wrap.addEventListener('click', (e) => {
      const tabEl = e.target.closest(TAB_SEL);
      if (!tabEl || !wrap.contains(tabEl)) return;
      e.preventDefault();
      setActiveTabByButton(wrap, tabEl);
    });
  }

  function syncTabsFromGroups(wrap, byTid){
    const buttons = qsa(wrap, TAB_SEL);
    let firstVisibleBtn = null;

    buttons.forEach(btn => {
      const tid = String(btn.getAttribute('data-term-id') || '0');
      const has = byTid.has(tid) && (byTid.get(tid).items || []).length > 0;
      const controls = btn.getAttribute('aria-controls');
      const panel = controls
        ? qs(wrap, '#' + CSS.escape(controls))
        : qs(wrap, `${PANEL_SEL}[data-term-id="${CSS.escape(tid)}"]`);

      btn.style.display   = has ? '' : 'none';
      if (panel) panel.style.display = has ? '' : 'none';
      if (has && !firstVisibleBtn) firstVisibleBtn = btn;
    });

    const currentActive = qs(wrap, `${TAB_SEL}.${ACTIVE}`);
    if (!currentActive || currentActive.style.display === 'none') {
      if (firstVisibleBtn) setActiveTabByButton(wrap, firstVisibleBtn);
    }
  }

  function syncTabsFromPanels(wrap){
    const buttons = qsa(wrap, TAB_SEL);
    let firstVisibleBtn = null;

    buttons.forEach(btn => {
      const tid = String(btn.getAttribute('data-term-id') || '0');
      const controls = btn.getAttribute('aria-controls');
      const panel = controls
        ? qs(wrap, '#' + CSS.escape(controls))
        : qs(wrap, `${PANEL_SEL}[data-term-id="${CSS.escape(tid)}"]`);
      const has = !!panel && qsa(panel, '[data-farlo-ui-faq-item]').some(i => i.style.display !== 'none');

      btn.style.display   = has ? '' : 'none';
      if (panel) panel.style.display = has ? '' : 'none';
      if (has && !firstVisibleBtn) firstVisibleBtn = btn;
    });

    const currentActive = qs(wrap, `${TAB_SEL}.${ACTIVE}`);
    if (!currentActive || currentActive.style.display === 'none') {
      if (firstVisibleBtn) setActiveTabByButton(wrap, firstVisibleBtn);
    }
  }

  function toggleFaqLocal(q){
    const item = q?.parentElement;
    const ans  = q?.nextElementSibling;
    if (!item || !ans) return;

    const open = !(q.dataset.toggled === '1');
    q.dataset.toggled = open ? '1' : '0';
    item.classList.toggle('rank-math-list-item--active', open);

    ans.removeAttribute('hidden');
    ans.offsetHeight; 
    const px = (ans.scrollHeight + 20) + 'px';
    ans.style.height = open ? px : '0px';
  }

  function bindFAQDelegation(wrap){
    if (wrap.dataset.faqDelegated) return;
    wrap.dataset.faqDelegated = '1';
    wrap.addEventListener('click', (e) => {
      if (e.target.closest(CHILD_ANCHOR_SEL)) return;
      const q = e.target.closest('.rank-math-question');
      if (!q || !wrap.contains(q)) return;
      e.preventDefault();
      toggleFaqLocal(q);
    });
  }

  function clearChildActive(root){
    qsa(root, CHILD_ANCHOR_SEL).forEach(a => a.classList.remove('is-active'));
    qsa(root, FAQ_GROUP_SEL).forEach(g => g.classList.remove('is-active'));
  }
  function activateChildForHash(hash, root){
    if (!hash) return false;
    const id = hash.slice(1);
    const target = document.getElementById(id);
    if (!target || (root && !root.contains(target))) return false;
    clearChildActive(root || document);
    const link = (root || document).querySelector(`${CHILD_ANCHOR_SEL}[href="#${CSS.escape(id)}"]`);
    if (link) link.classList.add('is-active');
    const group = target.closest(FAQ_GROUP_SEL);
    if (group) group.classList.add('is-active');
    smoothScrollTo(target);
    return true;
  }
  function bindChildAnchors(wrap){
    wrap.addEventListener('click', (e) => {
      const a = e.target.closest(CHILD_ANCHOR_SEL);
      if (!a || !wrap.contains(a)) return;
      const hash = a.getAttribute('href') || '';
      if (!hash.startsWith('#')) return;
      e.preventDefault();
      history.pushState(null, '', hash);
      activateChildForHash(hash, wrap);
    });
    const tryFromLocation = () => {
      const h = window.location.hash || '';
      if (!h) return;
      const t = document.getElementById(h.slice(1));
      if (t && wrap.contains(t)) activateChildForHash(h, wrap);
    };
    requestAnimationFrame(tryFromLocation);
    window.addEventListener('hashchange', tryFromLocation);
  }

  function renderGroupsIntoTabs(wrap, groups){
    const byTid = new Map(groups.map(g => [ String(g.term_id), g ]));
    qsa(wrap, PANEL_SEL).forEach(panel => {
      const termId = String(panel.getAttribute('data-term-id') || '0');
      const list   = qs(panel, '[data-farlo-ui-faq-list]');
      const empty  = qs(panel, '[data-farlo-ui-no-results]');
      if (list) list.innerHTML = '';
      const group = byTid.get(termId);
      if (group && group.items && group.items.length) {
        const frag = document.createDocumentFragment();
        group.items.forEach(item => {
          const div = document.createElement('div');
          div.id = item.slug || ('faq-question-' + String(item.id));
          div.className = 'rank-math-list-item';
          div.setAttribute('data-farlo-ui-faq-item', '');
          div.setAttribute('data-faq-id', String(item.id));
          const qEl = document.createElement('div');
          qEl.className = 'rank-math-question';
          qEl.textContent = item.question || '';
          qEl.dataset.toggled = '0';
          const aEl = document.createElement('div');
          aEl.className = 'rank-math-answer';
          aEl.innerHTML = item.answer || '';
          aEl.removeAttribute('hidden');
          aEl.style.height = '0px';
          div.appendChild(qEl);
          div.appendChild(aEl);
          frag.appendChild(div);
        });
        if (list) list.appendChild(frag);
        if (empty) empty.hidden = true;
      } else {
        if (empty) empty.hidden = false;
      }
    });
    syncTabsFromGroups(wrap, byTid);
    wrap.dispatchEvent(new CustomEvent('farlo:faq:updated', { bubbles: true }));
  }

  function setupInstance(wrap){
    const ajaxUrl   = wrap.getAttribute('data-ajax-url') || '';
    const nonce     = wrap.getAttribute('data-nonce') || '';
    const postType  = wrap.getAttribute('data-pt') || 'farlo-faq';
    const blockRoot = wrap.closest('.rank-math-block') || wrap.parentElement || document;
    const input     = qs(blockRoot, '[data-farlo-ui-faq-search]');

    bindTabDelegation(wrap);
    enforceFirstTabAfterPaint(wrap);
    bindFAQDelegation(wrap);
    bindChildAnchors(wrap);

    if (!input) return;

    if (!ajaxUrl) {
      const doFilter = debounce(() => {
        const needle = input.value.trim().toLowerCase();
        qsa(wrap, '[data-farlo-ui-faq-list]').forEach(list => {
          let visible = 0;
          qsa(list, '[data-farlo-ui-faq-item]').forEach(item => {
            const q = qs(item, '.rank-math-question')?.textContent.toLowerCase() || '';
            const a = qs(item, '.rank-math-answer')?.textContent.toLowerCase() || '';
            const match = !needle || q.includes(needle) || a.includes(needle);
            item.style.display = match ? '' : 'none';
            if (match) visible++;
          });
          const panel = list.closest(PANEL_SEL);
          const empty = qs(panel, '[data-farlo-ui-no-results]');
          if (empty) empty.hidden = visible !== 0;
        });
        syncTabsFromPanels(wrap);
      }, 250);
      input.addEventListener('input', doFilter);
      return;
    }

    const doAjaxSearch = debounce(() => {
      const s = input.value.trim();
      const body = new URLSearchParams();
      body.set('action', 'farlo_faq_search');
      body.set('nonce',  nonce);
      body.set('pt',     postType);
      body.set('s',      s);
      wrap.setAttribute('aria-busy', 'true');
      fetch(ajaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        body: body.toString()
      })
      .then(async r => {
        const text = await r.text();
        try { return JSON.parse(text); }
        catch { throw new Error(`Non-JSON response: ${text.slice(0,200)}`); }
      })
      .then(data => {
        if (!data?.success) throw new Error('Request failed');
        renderGroupsIntoTabs(wrap, data.data.groups || []);
      })
      .catch(err => console.error('FAQ search error:', err))
      .finally(() => wrap.removeAttribute('aria-busy'));
    }, 300);

    input.addEventListener('input', doAjaxSearch);
  }

  function initFarloFAQ(){
    const wrappers = qsa(document, '[data-farlo-ui-faq]');
    wrappers.forEach(wrap => setupInstance(wrap));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initFarloFAQ();
      requestAnimationFrame(() => {
        qsa(document, '[data-farlo-ui-faq]').forEach(wrap => enforceFirstTabAfterPaint(wrap));
      });
    });
  } else {
    initFarloFAQ();
    requestAnimationFrame(() => {
      qsa(document, '[data-farlo-ui-faq]').forEach(wrap => enforceFirstTabAfterPaint(wrap));
    });
  }
}
